import { escapeHtml } from '../util/escapeHtml.js';
import { store } from '../state/store.js';
import { ResourceBar } from './ResourceBar.js';
import { navigate } from '../router.js';
import { api } from '../api/client.js';
import { formatCrownsFull } from '../util/formatBig.js';
import { helpForCurrentRoute } from '../util/wikiHelp.js';
import { buildComplineRow } from './ComplineBanner.js';
import type { CharacterDto, ResourceName, HouseChainDto, ActivityStatusResponse } from '@rex-mortis/shared';

interface NavItem { href: string; label: string }
interface NavGroup { heading: string; items: NavItem[] }

// The sidebar is grouped into themed sections so pages are easier to find.
// Within each group items run roughly most- to least-used.
const NAV_GROUPS: NavGroup[] = [
  {
    heading: 'Character',
    items: [
      { href: '#/profile', label: 'Profile' },
      { href: '#/daily', label: 'Daily Tasks' },
      { href: '#/inventory', label: 'Inventory' },
    ],
  },
  {
    heading: 'Train & Fight',
    items: [
      { href: '#/training', label: 'Training Yard' },
      { href: '#/sparring', label: 'Sparring Hall' },
      { href: '#/attacks', label: 'Attacks' },
      { href: '#/crypt', label: 'The Crypt' },
      { href: '#/shades-due', label: "The Shade's Due" },
    ],
  },
  {
    heading: 'Livelihood',
    items: [
      { href: '#/misdeeds', label: 'Misdeeds' },
      { href: '#/good-deeds', label: 'Good Deeds' },
      { href: '#/scholarium', label: 'Scholarium' },
      { href: '#/trades', label: 'Trades' },
      { href: '#/forge', label: 'The Forge' },
      { href: '#/apothecary', label: 'The Apothecary' },
      { href: '#/cookfire', label: 'The Cookfire' },
      { href: '#/brewhouse', label: 'The Brewhouse' },
      { href: '#/expeditions', label: 'Expeditions' },
    ],
  },
  {
    heading: 'Commerce',
    items: [
      { href: '#/market', label: 'Merchant’s Market' },
      { href: '#/banks', label: 'Counting House' },
      { href: '#/travel', label: 'The Roads' },
      { href: '#/domiciles', label: 'Domiciles' },
      { href: '#/membership', label: 'The Royal Charter' },
    ],
  },
  {
    heading: 'Society',
    items: [
      { href: '#/shoutbox', label: 'The Shoutbox' },
      { href: '#/mail', label: 'The Post' },
      { href: '#/friends', label: 'Acquaintances' },
      { href: '#/houses', label: 'Houses' },
    ],
  },
  {
    heading: 'Sport & Glory',
    items: [
      { href: '#/racing', label: 'The Racing Yard' },
      { href: '#/awards', label: 'Awards' },
      { href: '#/leaderboards', label: 'The Annals' },
      { href: '#/replays', label: 'Replays' },
      { href: '#/events', label: 'The Calendar' },
    ],
  },
  {
    heading: 'Recovery',
    items: [
      { href: '#/infirmary', label: 'Infirmary' },
      { href: '#/dungeon', label: 'Dungeon' },
    ],
  },
  {
    heading: 'Help & Account',
    items: [
      { href: '#/wiki', label: 'The Compendium' },
      { href: '#/patch-notes', label: 'Patch Notes' },
      { href: '#/rules', label: 'Rules of the Realm' },
      { href: '#/settings', label: 'Settings' },
    ],
  },
];

// Regen rates (points/sec) for the "ready to spend" glow projection. Must match
// server REGEN and client ResourceBar.ts.
const NAV_VIGOR_PER_SEC = 5 / (4 * 60);
const NAV_RESOLVE_PER_SEC = 1 / 70;

// "Ready to collect" completion times for Scholarium / Expeditions / Racing,
// polled (see mountShell) on the same ~30s cadence as the bell. The sidebar
// lights the matching tab once the timestamp passes — projected per-second by
// refreshNavAttention, same as the resource glow. Module-level so a header
// rebuild keeps the last-known state without a flicker.
let activityReady: ActivityStatusResponse = {
  scholariumReadyAt: null,
  expeditionReadyAt: null,
  raceReadyAt: null,
};
let lastActivityFetch = 0;
async function refreshActivityStatus(): Promise<void> {
  try {
    activityReady = await api.activityStatus();
  } catch {
    // ignored — keep the previous state
  }
}

// "Now in the realm" presence panel — souls seen in the last 15 minutes.
// Fetched on a ~60s cadence by the sidebar ticker; module-level so a shell
// rebuild keeps the last-known roster without a flicker. This is a density
// feature: players who can SEE each other online stop feeling alone.
let presenceRoster: Array<{ characterId: string; name: string; level: number; minutesAgo: number }> = [];
let presenceTotal = 0;
let lastPresenceFetch = 0;
async function refreshPresence(): Promise<void> {
  try {
    const r = await api.recentlyActive();
    presenceRoster = r.active;
    presenceTotal = r.total;
  } catch {
    // ignored — keep the previous roster
  }
}

// Compact panel: a label + a clickable count. Long names used to overflow the
// narrow sidebar; the roster now lives behind a click (openPresenceModal), so
// the panel itself stays one tidy line however long anyone's name is.
function renderPresencePanel(): string {
  const count = presenceTotal;
  // A plain clickable label (NOT a <button> — that picked up the global
  // form-control chrome and rendered as a white box). Reuses .sidebar-cd-label
  // so it matches "Chain" / "Next milestone" above it exactly.
  return (
    `<div class="sidebar-cd-title" title="Souls seen in the realm within the last 15 minutes. Click to see who.">Souls currently in the realm</div>` +
    `<div class="sidebar-cd-row">` +
    `<span class="sidebar-cd-label presence-count" role="button" tabindex="0" aria-haspopup="dialog" aria-label="See who is in the realm">${count} Souls Online</span>` +
    `</div>`
  );
}

function ensurePresenceStyles(): void {
  if (document.getElementById('presence-styles')) return;
  const style = document.createElement('style');
  style.id = 'presence-styles';
  // All colours use theme tokens that flip together (parchment/ink/oxblood/gold),
  // so contrast holds in both themes — no pinned literals needed.
  style.textContent = `
    /* Inherits .sidebar-cd-label (font-elegant, --c-ink-soft) so it sits flush
       with Chain / Next milestone above. Just make it read as clickable. */
    .sidebar-presence .presence-count { cursor: pointer; }
    .sidebar-presence .presence-count:hover { color: var(--c-oxblood); text-decoration: underline; text-decoration-style: dotted; }

    #presence-overlay { position: fixed; inset: 0; background: rgba(28,17,8,0.55); z-index: 300; backdrop-filter: blur(1.5px); }
    #presence-modal {
      position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
      width: min(380px, 92vw); max-height: 80vh; overflow-y: auto;
      background: var(--c-parchment); background-image: var(--parchment-texture);
      border: 1px solid var(--c-rule-strong); border-radius: var(--radius-lg);
      box-shadow: 0 0 0 1px var(--c-gold), 0 14px 40px rgba(0,0,0,0.5);
      padding: var(--s-5); z-index: 301;
    }
    #presence-modal .pm-title {
      font-family: var(--font-heading); font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
      font-size: 1.15rem; color: var(--c-oxblood); margin: 0 0 var(--s-3); text-align: center;
    }
    #presence-modal .pm-row {
      display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
      padding: 7px 2px; border-bottom: 1px solid var(--c-rule);
    }
    #presence-modal .pm-row:last-of-type { border-bottom: none; }
    #presence-modal .pm-name { font-family: var(--font-elegant, Georgia, serif); font-size: 1rem; color: var(--c-ink); text-decoration: none; min-width: 0; overflow-wrap: anywhere; }
    #presence-modal a.pm-name:hover { color: var(--c-oxblood); text-decoration: underline; }
    #presence-modal .pm-when { font-family: var(--font-numeric, monospace); font-size: 0.8rem; color: var(--c-ink-soft); flex: none; white-space: nowrap; }
    #presence-modal .pm-overflow { font-style: italic; color: var(--c-ink-soft); text-align: center; margin-top: var(--s-3); font-size: 0.9rem; }
    #presence-modal .pm-close { display: block; margin: var(--s-4) auto 0; }
  `;
  document.head.append(style);
}

// The roster popup: the six freshest names (as the sidebar showed before), each
// a link to its profile, plus the "and N more abroad" tally as the realm grows.
function openPresenceModal(): void {
  if (document.getElementById('presence-overlay')) return; // already open
  ensurePresenceStyles();
  const me = store.get().character?.id;

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    document.getElementById('presence-overlay')?.remove();
    document.getElementById('presence-modal')?.remove();
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  const overlay = document.createElement('div');
  overlay.id = 'presence-overlay';
  overlay.addEventListener('click', close);
  document.body.append(overlay);

  const modal = document.createElement('div');
  modal.id = 'presence-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-label', 'Souls in the realm');
  modal.addEventListener('click', (e) => e.stopPropagation());
  document.body.append(modal);

  const rows = presenceRoster
    .map((p) => {
      const when = p.minutesAgo < 2 ? 'now' : `${p.minutesAgo}m ago`;
      const name = p.characterId === me
        ? `<span class="pm-name">${escapeHtml(p.name)} (you)</span>`
        : `<a class="pm-name" href="#/u/${p.characterId}">${escapeHtml(p.name)}</a>`;
      return `<div class="pm-row">${name}<span class="pm-when">${when}</span></div>`;
    })
    .join('');
  const overflow = presenceTotal > presenceRoster.length
    ? `<div class="pm-overflow">…and ${presenceTotal - presenceRoster.length} more abroad</div>`
    : '';
  const body = rows || `<div class="pm-overflow">The square lies quiet.</div>`;

  modal.innerHTML =
    `<h2 class="pm-title">Souls in the realm</h2>${body}${overflow}` +
    `<button type="button" class="btn ghost pm-close" id="pm-close">Close</button>`;
  modal.querySelector<HTMLButtonElement>('#pm-close')?.addEventListener('click', close);
  // Following a profile link changes the hash route underneath us — close first.
  modal.querySelectorAll('a.pm-name').forEach((a) => a.addEventListener('click', () => close()));
  document.addEventListener('keydown', onKey);
}

/**
 * Toggle the gold "ready" glow on the action links when their resource is
 * (projected to be) full: Training Yard + Sparring Hall on full Vigor, Misdeeds
 * + Good Deeds on full Resolve. Projects from the last server measurement just
 * like the resource bars, so the glow appears the instant a bar tops out and
 * clears the moment you spend below max. Cheap — safe to call every second.
 */
function refreshNavAttention(shell: HTMLElement): void {
  const c = store.get().character;
  if (!c) return;
  const asOf = store.get().characterAsOf ?? Date.now();
  const elapsed = Math.max(0, (Date.now() - asOf) / 1000);
  const vigorFull = c.vigor + NAV_VIGOR_PER_SEC * elapsed >= c.vigorMax;
  const resolveFull = c.resolve + NAV_RESOLVE_PER_SEC * elapsed >= c.resolveMax;
  const set = (href: string, on: boolean): void => {
    const a = shell.querySelector<HTMLAnchorElement>(`.shell-sidebar a[href="${href}"]`);
    if (a) a.classList.toggle('nav-attn', on);
  };
  set('#/training', vigorFull);
  set('#/sparring', vigorFull);
  set('#/misdeeds', resolveFull);
  set('#/good-deeds', resolveFull);
  // Timed activities: light the tab once the completion time has passed and
  // there are rewards waiting to be claimed.
  const due = (iso: string | null): boolean => iso != null && Date.parse(iso) <= Date.now();
  set('#/scholarium', due(activityReady.scholariumReadyAt));
  set('#/expeditions', due(activityReady.expeditionReadyAt));
  set('#/racing', due(activityReady.raceReadyAt));
}

/**
 * Crown currency icon — the custom Rex Mortis crown art. Paired with the
 * player's crown total in the sidebar treasury box. Falls back to hidden if
 * the asset is missing (no broken-image box).
 */
const CROWN_ICON_SVG = `
  <img src="/img/ui/crown.webp" width="28" height="28" alt="" aria-hidden="true"
       style="vertical-align: -7px; margin-right: 6px; flex-shrink: 0; object-fit: contain;"
       onerror="this.style.display='none'" />
`;

/**
 * Status glyphs — Unicode characters with U+FE0E to force text-style rendering.
 * Exported so the Profile page can reuse the same visual vocabulary.
 */
export const STATUS_GLYPH: Record<string, string> = {
  OKAY:       '❦︎',  // floral heart — alive and well
  INFIRMARY:  '✚︎',  // heavy medical cross — hurt, recovering
  DUNGEON:    '⛓︎',  // chains — jailed
  TRAVELLING: '⚓︎',  // anchor — on the road
};

function statusLine(char: CharacterDto): { glyph: string; phrase: string; until: string | null } | null {
  if (char.status === 'OKAY') return null;
  if (char.status === 'INFIRMARY') {
    return { glyph: STATUS_GLYPH.INFIRMARY ?? '', phrase: 'In the Infirmary', until: char.statusUntil ?? null };
  }
  if (char.status === 'DUNGEON') {
    return { glyph: STATUS_GLYPH.DUNGEON ?? '', phrase: 'In the Dungeon', until: char.statusUntil ?? null };
  }
  if (char.status === 'TRAVELLING') {
    return { glyph: STATUS_GLYPH.TRAVELLING ?? '', phrase: 'On the road', until: char.statusUntil ?? null };
  }
  return null;
}

/** Status-banner countdown — always shows seconds so it visibly ticks. */
function fmtStatusCountdown(ms: number): string {
  if (ms <= 0) return 'any moment now';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Module-level status-banner ticker. The header rebuilds on every store
 *  update, so the old interval is cleared and a fresh one started per build.
 *  When the countdown hits zero we poll /me once — the server resolves the
 *  arrival/release lazily, and the resulting store update rebuilds the header
 *  without the banner (no page refresh needed). */
let statusTickHandle: number | null = null;
let statusRefreshing = false;

/**
 * Module-level bell poll state. `mountShell` rebuilds the whole header on every
 * store update (every fight, crime, navigation, resource tick), so `buildBell`
 * runs constantly. Without this shared state each rebuild would spawn its own
 * 30s interval AND fire an immediate fetch — flooding the server with
 * /notifications/unread-count requests. Instead we keep exactly ONE poll loop
 * and ONE document-click listener app-wide, and seed each freshly-built badge
 * from the cached count so it never flickers to empty on a rebuild.
 */
let bellPollHandle: number | null = null;
let bellDocClickHandler: ((ev: MouseEvent) => void) | null = null;
let bellRefreshHandler: (() => void) | null = null;
let lastUnreadCount = 0;

/** Nudge the notification bell to re-fetch its unread count immediately, rather
 *  than waiting for the 30s poll — fire after any client action that changes the
 *  player's own unread set (e.g. opening House chat clears its bell entries). */
export function refreshNotificationBell(): void {
  window.dispatchEvent(new Event('rm:notif-refresh'));
}

/**
 * Notification bell — sits in the header showing an unread count.
 * Click to open a small dropdown listing recent notifications. Clicking a
 * notification marks it read and navigates to its link.
 * Polls /notifications/unread-count every 30 seconds to keep the badge live.
 */
function buildBell(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'bell-wrap';

  const btn = document.createElement('button');
  btn.className = 'bell-btn';
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Notifications');
  btn.innerHTML = '<span class="bell-glyph" aria-hidden="true">✉</span><span class="bell-badge" hidden>0</span>';
  const badge = btn.querySelector<HTMLSpanElement>('.bell-badge')!;

  const menu = document.createElement('div');
  menu.className = 'bell-menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  const setBadge = (n: number) => {
    lastUnreadCount = n; // cache so a header rebuild can seed the badge offline
    if (n > 0) {
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  };

  const refreshCount = async (): Promise<void> => {
    try {
      const r = await api.notificationsUnreadCount();
      setBadge(r.unreadCount);
    } catch {
      // ignored — keep the previous badge
    }
  };

  const renderMenu = (notifs: { id: string; kind: string; message: string; link: string | null; readAt: string | null; createdAt: string }[]) => {
    if (notifs.length === 0) {
      menu.innerHTML = '<div class="bell-empty">No notifications.</div>';
      return;
    }
    menu.innerHTML = `
      <div class="bell-head">
        <span>Notifications</span>
        <button class="bell-mark-all" type="button">Mark all read</button>
      </div>
      <div class="bell-list">
        ${notifs.map((n) => `
          <button class="bell-item ${n.readAt ? '' : 'unread'}" data-id="${n.id}" data-link="${n.link ?? ''}" type="button">
            <span class="bell-item-icon" aria-hidden="true">${iconForKind(n.kind)}</span>
            <span class="bell-item-body">
              <span class="bell-item-msg">${escapeHtmlBell(n.message)}</span>
              <span class="bell-item-time">${formatRelativeBell(n.createdAt)}</span>
            </span>
          </button>
        `).join('')}
      </div>
    `;

    menu.querySelector<HTMLButtonElement>('.bell-mark-all')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      try {
        await api.markAllNotificationsRead();
        setBadge(0);
        // Re-fetch list so styling updates.
        await openMenu();
      } catch { /* ignore */ }
    });

    menu.querySelectorAll<HTMLButtonElement>('.bell-item').forEach((item) => {
      item.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = item.dataset.id!;
        const link = item.dataset.link || null;
        try {
          await api.markNotificationRead(id);
        } catch { /* ignore */ }
        closeMenu();
        await refreshCount();
        if (link) {
          // Hash routes use a leading '#'. Replace the hash to navigate.
          if (link.startsWith('#')) window.location.hash = link.slice(1);
          else window.location.hash = link;
        }
      });
    });
  };

  const openMenu = async (): Promise<void> => {
    menu.hidden = false;
    wrap.dataset.open = 'true';
    try {
      const r = await api.listNotifications({ limit: 12 });
      renderMenu(r.notifications);
      setBadge(r.unreadCount);
    } catch {
      renderMenu([]);
    }
  };
  const closeMenu = (): void => {
    menu.hidden = true;
    wrap.dataset.open = 'false';
  };

  btn.addEventListener('click', async (e) => {
    e.stopPropagation();
    if (wrap.dataset.open === 'true') closeMenu();
    else await openMenu();
  });

  // Close on outside click. Only ONE such listener exists app-wide — the header
  // is rebuilt on every store update, so drop the previous one before adding.
  const onDocClick = (ev: MouseEvent) => {
    if (!wrap.contains(ev.target as Node)) closeMenu();
  };
  if (bellDocClickHandler) document.removeEventListener('click', bellDocClickHandler);
  bellDocClickHandler = onDocClick;
  document.addEventListener('click', onDocClick);

  // Immediate badge refresh on demand (refreshNotificationBell()). Re-bound per
  // header rebuild, like the outside-click handler above.
  if (bellRefreshHandler) window.removeEventListener('rm:notif-refresh', bellRefreshHandler);
  bellRefreshHandler = () => { void refreshCount(); };
  window.addEventListener('rm:notif-refresh', bellRefreshHandler);

  // Seed the badge from the cached count — no network call — so a header
  // rebuild never flickers the badge to empty.
  setBadge(lastUnreadCount);

  // Exactly ONE poll loop exists app-wide. A genuine cold start (no loop yet)
  // does a single immediate fetch; a header rebuild just adopts the loop by
  // clearing the prior interval and starting a fresh one (no extra fetch).
  if (bellPollHandle === null) {
    void refreshCount();
  } else {
    window.clearInterval(bellPollHandle);
    bellPollHandle = null;
  }
  bellPollHandle = window.setInterval(() => {
    // Bell unmounted without a rebuild (e.g. logout) — stop polling entirely.
    if (!wrap.isConnected) {
      if (bellPollHandle !== null) {
        window.clearInterval(bellPollHandle);
        bellPollHandle = null;
      }
      if (bellDocClickHandler) {
        document.removeEventListener('click', bellDocClickHandler);
        bellDocClickHandler = null;
      }
      if (bellRefreshHandler) {
        window.removeEventListener('rm:notif-refresh', bellRefreshHandler);
        bellRefreshHandler = null;
      }
      return;
    }
    void refreshCount();
  }, 30_000);

  wrap.append(btn, menu);
  return wrap;
}

function iconForKind(kind: string): string {
  switch (kind) {
    case 'mail': return '✉';
    case 'pvp_attack': return '⚔';
    case 'expedition_ready': return '✦';
    case 'war_declared': return '⚔';
    case 'holding_lost': return '☠';
    case 'holding_won': return '⚜';
    case 'house_chat': return '💬';
    default: return '◆';
  }
}

function formatRelativeBell(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function escapeHtmlBell(s: string): string {
  return s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function buildHeader(char: CharacterDto): HTMLElement {
  const header = document.createElement('header');
  header.className = 'shell-header';

  // Hamburger toggle — only visible on narrow viewports via CSS.
  const burger = document.createElement('button');
  burger.className = 'shell-burger';
  burger.type = 'button';
  burger.setAttribute('aria-label', 'Toggle navigation');
  burger.innerHTML = '<span></span><span></span><span></span>';
  burger.addEventListener('click', () => {
    const shell = document.querySelector('.shell');
    const sidebar = document.querySelector('.shell-sidebar');
    if (!shell || !sidebar) return;
    const open = sidebar.getAttribute('data-mobile-open') === 'true';
    sidebar.setAttribute('data-mobile-open', String(!open));
    shell.setAttribute('data-mobile-nav', String(!open));
  });

  const crest = document.createElement('div');
  crest.className = 'crest';
  // The wordmark logo stands alone (it carries its own crown emblem) — the old
  // shield medallion was redundant and has been dropped from the header.
  const crestLogo = document.createElement('img');
  crestLogo.className = 'crest-logo';
  crestLogo.src = '/img/ui/title.webp';
  crestLogo.alt = 'Rex Mortis';
  crestLogo.style.verticalAlign = 'middle';
  crestLogo.addEventListener('error', () => { crestLogo.style.display = 'none'; });
  crest.append(crestLogo);

  // Crown total now lives in the sidebar (above Profile); level lives on the
  // Profile page. The header carries only the crest, resource bars, and bell.
  const bars = document.createElement('div');
  bars.className = 'bars';
  const ladder: ResourceName[] = ['vigor', 'resolve', 'spirit', 'life'];
  // Anchor each bar's regen prediction to WHEN the server last measured these
  // values, so a header rebuild mid-session doesn't reset the tick to a stale
  // baseline (which made the bars under-count regen until a page refresh).
  const asOf = store.get().characterAsOf ?? Date.now();
  for (const k of ladder) {
    const cur = char[k];
    const max = char[`${k}Max` as const];
    bars.append(ResourceBar({ kind: k, current: cur, max, asOf }));
  }

  // Refill tokens are spent per-bar from the Profile page (Vigor / Resolve
  // pills) — no global "fill everything" button in the header any more.

  const bell = buildBell();

  // Mobile layout: the Rex Mortis wordmark (crest) sits at the very top of the
  // page in normal flow — you scroll PAST it as you go down. Below it, the
  // resource strip + a thin action row (hamburger left, bell right) form a
  // sticky bar that FOLLOWS you down the page, keeping the menu + notifications
  // always reachable. On desktop both wrappers are `display: contents`, so
  // burger/bars/crest/bell lay out as direct header flex children as before.
  const topbar = document.createElement('div');
  topbar.className = 'mobile-topbar';
  const actions = document.createElement('div');
  actions.className = 'topbar-actions';
  actions.append(burger, bell);
  // Mobile: a "?" help link rides in the sticky bar beside the bell, so the
  // Compendium shortcut follows you down the page. Hidden on desktop, which
  // uses the floating .page-help button instead. Route-aware.
  const barHelp = helpForCurrentRoute();
  if (barHelp) {
    const h = document.createElement('a');
    h.className = 'topbar-help';
    h.href = `#/wiki?s=${barHelp.slug}`;
    h.textContent = '?';
    h.setAttribute('aria-label', `Open the Compendium: ${barHelp.title}`);
    h.title = `Help — ${barHelp.title}`;
    actions.append(h);
  }
  topbar.append(bars, actions);

  header.append(crest, topbar);

  // Status banner (Infirmary, Dungeon, Travelling) — sits below the bars when
  // active. Shows a LIVE countdown, and clears itself the moment it lapses.
  if (statusTickHandle !== null) {
    window.clearInterval(statusTickHandle);
    statusTickHandle = null;
  }
  const sl = statusLine(char);
  if (sl) {
    const banner = document.createElement('div');
    banner.style.flexBasis = '100%';
    banner.style.padding = '6px 0 0 0';
    banner.style.color = 'var(--c-oxblood)';
    banner.style.fontFamily = 'var(--font-display)';
    banner.style.fontSize = '0.95rem';
    const untilMs = sl.until ? Date.parse(sl.until) : null;
    banner.innerHTML =
      `<span style="font-size: 1.15em; margin-right: 6px;">${sl.glyph}</span>${sl.phrase}` +
      (untilMs !== null
        ? ` — <span class="status-cd" style="font-family: var(--font-numeric);">${fmtStatusCountdown(untilMs - Date.now())}</span>`
        : '');
    header.append(banner);

    if (untilMs !== null) {
      const cdEl = banner.querySelector<HTMLSpanElement>('.status-cd')!;
      statusTickHandle = window.setInterval(() => {
        // The header was rebuilt and this banner discarded — stop ticking.
        if (!banner.isConnected) {
          if (statusTickHandle !== null) {
            window.clearInterval(statusTickHandle);
            statusTickHandle = null;
          }
          return;
        }
        const ms = untilMs - Date.now();
        cdEl.textContent = fmtStatusCountdown(ms);
        if (ms <= 0 && !statusRefreshing) {
          // Lapsed — one /me poll resolves the arrival/release server-side and
          // the store update rebuilds the header without this banner.
          statusRefreshing = true;
          void api
            .me()
            .then((r) => store.set({ account: r.account, character: r.character }))
            .catch(() => { /* transient — the next poll or action will clear it */ })
            .finally(() => {
              statusRefreshing = false;
            });
        }
      }, 1000);
    }
  }

  // Email-verification banner — visible until the user confirms their address.
  const account = store.get().account;
  if (account && !account.emailVerified) {
    const banner = document.createElement('div');
    banner.style.flexBasis = '100%';
    banner.style.padding = '6px 8px';
    banner.style.marginTop = '6px';
    banner.style.background = 'rgba(212, 169, 60, 0.18)';
    banner.style.border = '1px solid var(--c-gold)';
    banner.style.borderRadius = 'var(--radius)';
    banner.style.display = 'flex';
    banner.style.alignItems = 'center';
    banner.style.gap = '10px';
    banner.style.justifyContent = 'space-between';
    banner.style.flexWrap = 'wrap';

    const msg = document.createElement('span');
    msg.style.fontFamily = 'var(--font-elegant)';
    msg.style.fontStyle = 'italic';
    msg.style.fontSize = '0.9rem';
    msg.style.color = 'var(--c-ink)';
    msg.innerHTML = `📜 Confirm thy email <strong>${escapeHtml(account.email)}</strong> so the Heralds may find thee. Check thy inbox for the scroll.`;

    const resend = document.createElement('button');
    resend.className = 'btn';
    resend.style.padding = '4px 10px';
    resend.style.fontSize = '0.8rem';
    resend.textContent = 'Resend scroll';
    resend.addEventListener('click', async () => {
      resend.disabled = true;
      const original = resend.textContent;
      resend.textContent = 'Sending…';
      try {
        await api.resendVerification();
        resend.textContent = 'Sent ✓';
        setTimeout(() => {
          resend.textContent = original;
          resend.disabled = false;
        }, 3000);
      } catch {
        resend.textContent = 'Failed — retry';
        resend.disabled = false;
      }
    });

    banner.append(msg, resend);
    header.append(banner);
  }

  // Compline bell — a full-width row below the bars while the evening blessing
  // rings. Rendered from ComplineBanner's cache so it survives header rebuilds;
  // the poll/ticker there keeps it fresh and re-attaches it after navigation.
  const complineRow = buildComplineRow();
  if (complineRow) header.append(complineRow);

  return header;
}

function buildSidebar(char: CharacterDto): HTMLElement {
  const aside = document.createElement('aside');
  aside.className = 'shell-sidebar';
  aside.setAttribute('data-mobile-open', 'false');

  const nav = document.createElement('nav');
  const here = window.location.hash || '#/profile';

  // Crown total — a small heading above the nav. Full comma-separated value
  // below a million; short form ("1.2m") above. Updated in place by the
  // store subscription in mountShell (the sidebar itself isn't rebuilt).
  // Treasury box — the crown total plus the bank-interest countdown, together
  // in one bordered panel. The interest line is hidden (CSS :empty) when the
  // player has no bank; it's updated every second by the ticker in mountShell.
  const crowns = document.createElement('div');
  crowns.className = 'sidebar-crowns';
  crowns.innerHTML = `
    <div class="sidebar-crowns-row">${CROWN_ICON_SVG}<span class="sidebar-crowns-amount">${formatCrownsFull(char.crowns)}</span></div>
    <div class="sidebar-interest">${renderInterestLine(store.get().bankInterest)}</div>
  `;
  nav.append(crowns);

  // Item-cooldown panel — Elixir / Tonic / Medical. Always shows all three so
  // the player can see at a glance which are Ready and which are counting down.
  // Updated in place each second by the ticker in mountShell.
  const cooldowns = document.createElement('div');
  cooldowns.className = 'sidebar-cooldowns';
  cooldowns.innerHTML = renderCooldownsPanel(char);
  nav.append(cooldowns);

  // House bloodchain tracker — sits under item cooldowns. Empty (renders
  // nothing) when the player isn't in a House. Live-ticked alongside cooldowns.
  const chainPanel = document.createElement('div');
  chainPanel.className = 'sidebar-chain';
  chainPanel.innerHTML = renderChainPanel(store.get().chain);
  nav.append(chainPanel);

  // "Now in the realm" — who else walks the realm right now. Seeded from the
  // module cache; refreshed on the ticker cadence in mountShell.
  const presencePanel = document.createElement('div');
  presencePanel.className = 'sidebar-cooldowns sidebar-presence';
  presencePanel.innerHTML = renderPresencePanel();
  // Delegated so it survives the 60s innerHTML refresh (the panel element
  // persists; only its contents are swapped).
  presencePanel.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('.presence-count')) openPresenceModal();
  });
  presencePanel.addEventListener('keydown', (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).closest('.presence-count')) {
      e.preventDefault();
      openPresenceModal();
    }
  });
  nav.append(presencePanel);

  // Close the mobile drawer whenever the user clicks a nav link.
  const closeDrawer = () => {
    aside.setAttribute('data-mobile-open', 'false');
    document.querySelector('.shell')?.setAttribute('data-mobile-nav', 'false');
  };

  NAV_GROUPS.forEach((group, gi) => {
    const heading = document.createElement('div');
    heading.className = 'sidebar-group';
    heading.textContent = group.heading;
    // The first group sits just under the status panels; give the rest a little
    // more breathing room above their heading to separate the sections.
    heading.style.cssText =
      'font-family: var(--font-numeric); font-size: 0.66rem; font-weight: 700;' +
      ' letter-spacing: 0.1em; text-transform: uppercase; color: var(--c-ink-soft);' +
      ` opacity: 0.7; padding: 4px 10px; margin-top: ${gi === 0 ? 6 : 16}px;`;
    nav.append(heading);

    for (const item of group.items) {
      const a = document.createElement('a');
      a.href = item.href;
      a.textContent = item.label;
      if (here === item.href) a.classList.add('active');
      a.addEventListener('click', () => closeDrawer());
      nav.append(a);
    }
  });

  // Logout button
  const logout = document.createElement('a');
  logout.href = '#/login';
  logout.textContent = 'Sign out';
  logout.style.marginTop = '24px';
  logout.style.color = 'var(--c-oxblood)';
  logout.addEventListener('click', async (e) => {
    e.preventDefault();
    try {
      await api.logout();
    } catch {
      // ignore
    }
    store.clear();
    navigate('/login');
  });
  nav.append(logout);

  aside.append(nav);
  return aside;
}

/** Compact countdown text, e.g. "3h 12m", "12m 04s", "44s", or "due now". */
function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'due now';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** The sidebar interest line HTML, or '' when there's nothing to show. */
function renderInterestLine(
  bi: { nextInterestAt: string; perDayCrowns: string; toWallet: boolean } | null,
): string {
  if (!bi || bi.perDayCrowns === '0') return '';
  const ms = Date.parse(bi.nextInterestAt) - Date.now();
  const dest = bi.toWallet ? 'to wallet' : 'to vault';
  const tip = bi.toWallet
    ? 'Vault full — daily interest overflows into your wallet'
    : 'Daily bank interest, compounding into your vault';
  return (
    `<span class="sidebar-interest-label">Bank interest</span>` +
    `<span class="sidebar-interest-value" title="${tip}">+${formatCrownsFull(bi.perDayCrowns)} ${dest} · ${fmtCountdown(ms)}</span>`
  );
}

/** Sidebar item-cooldown panel: one row per class (Elixir/Tonic/Medical/
 *  Provisions), showing "Ready" when free or a live countdown when locked out. */
function renderCooldownsPanel(char: CharacterDto): string {
  const rows: Array<[string, string | null]> = [
    ['Elixir', char.elixirCooldownUntil],
    ['Tonic', char.tonicCooldownUntil],
    ['Medical', char.medicalCooldownUntil],
    ['Provisions', char.foodCooldownUntil],
  ];
  const body = rows
    .map(([label, until]) => {
      const ms = until ? Date.parse(until) - Date.now() : 0;
      const active = ms > 0;
      const val = active ? fmtCountdown(ms) : 'Ready';
      return (
        `<div class="sidebar-cd-row"><span class="sidebar-cd-label">${label}</span>` +
        `<span class="sidebar-cd-val ${active ? 'active' : 'ready'}">${val}</span></div>`
      );
    })
    .join('');
  return `<div class="sidebar-cd-title">Item cooldowns</div>${body}`;
}

/** Sidebar House-bloodchain tracker. The displayed chain is the *true streak*
 *  (House.chainCount), a monotonic counter that doesn't backslide on the
 *  rolling-window edge — it only resets when no House member has landed a win
 *  for `chainResetSeconds`. Alongside the count we show the next-milestone
 *  target (+10 / +25 / +50 / +100 / +250 / +500 / +1000) and a live reset
 *  countdown. The per-step Renown bonus (rolling 5-min) still drives the
 *  rate-of-attack reward server-side; it's surfaced via the panel tooltip
 *  rather than cluttering the display. Renders nothing when not in a House. */
function renderChainPanel(chain: HouseChainDto | null): string {
  if (!chain) return '';
  const now = Date.now();
  // Live streak: server returns the persisted count, but if the most-recent
  // House win is already past the reset window we treat the visible streak as
  // 0 (the next win will start a fresh chain).
  const lastAtMs = chain.streakLastAt ? Date.parse(chain.streakLastAt) : 0;
  const resetMs = chain.chainResetSeconds * 1000;
  const ageMs = lastAtMs ? now - lastAtMs : Infinity;
  const streak = ageMs > resetMs ? 0 : chain.streakCount;

  // Next milestone: smallest milestone strictly greater than the current
  // streak. When all are passed, show the top milestone as "max".
  const milestones = chain.milestones ?? [];
  const next = milestones.find((m) => m.at > streak);
  const cap = milestones.length > 0 ? milestones[milestones.length - 1]! : null;
  const atTop = !next && cap !== null && streak >= cap.at;

  // Countdown: how long the streak has left before it cools out.
  const coolMs = lastAtMs && streak > 0 ? Math.max(0, lastAtMs + resetMs - now) : 0;

  const streakVal = streak > 0 ? `${streak} hit${streak === 1 ? '' : 's'}` : 'Idle';
  const milestoneVal = next
    ? `+${next.bonus} at ${next.at}`
    : atTop
      ? `+${cap!.bonus} (max)`
      : '—';

  // Tooltip: every win pays +1 base Renown; the only chain reward is the
  // one-time milestone bump when the streak lands exactly on a marker.
  const tip =
    `House bloodchain: a true streak — only resets when no House member has landed a win for ${Math.round(resetMs / 60000)} min. ` +
    `Every win pays +1 Renown; landing the streak exactly on a milestone pays a one-time bonus: ` +
    `${milestones.map((m) => `${m.at} (+${m.bonus})`).join(', ')}.`;

  const rows =
    `<div class="sidebar-cd-row"><span class="sidebar-cd-label">Chain</span>` +
    `<span class="sidebar-cd-val ${streak > 0 ? 'active' : 'ready'}">${streakVal}</span></div>` +
    `<div class="sidebar-cd-row"><span class="sidebar-cd-label">Next milestone</span>` +
    `<span class="sidebar-cd-val ${next || atTop ? 'active' : 'ready'}">${milestoneVal}</span></div>` +
    (streak > 0
      ? `<div class="sidebar-cd-row"><span class="sidebar-cd-label">Resets in</span>` +
        `<span class="sidebar-cd-val">${fmtCountdown(coolMs)}</span></div>`
      : '');
  return `<div class="sidebar-cd-title" title="${tip}">House chain</div>${rows}`;
}

/** Module-level interest-ticker handle; only one runs at a time. */
let interestTicker: number | null = null;
let interestRefreshing = false;

// Cleanup for the PREVIOUS shell mount. The router swaps pages by clearing
// root.innerHTML and calling mountShell again WITHOUT invoking the returned
// cleanup, so each navigation used to leak a store.subscribe callback (rebuilding
// a full header into a detached shell on every store update) plus a document
// keydown listener. We run the prior cleanup at the start of every mount so only
// the live shell stays subscribed, regardless of whether callers use the return.
let lastShellCleanup: (() => void) | null = null;

/**
 * Mount the shell + a content renderer into root. The renderer receives the
 * <main> element and may rebuild it whenever the store changes.
 */
/**
 * Shell for pages that must remain readable WITHOUT a signed-in character —
 * the legal pages (Rules of the Realm, Terms of Service, Privacy Policy) are
 * linked from the landing and register pages. With a character present this
 * defers to the full game shell; for a signed-out visitor it renders a plain
 * centred column with a way back to the gates, instead of the blank page the
 * character-less mountShell early-return used to produce.
 */
export function mountShellOrStandalone(
  root: HTMLElement,
  renderMain: (main: HTMLElement) => void,
): void {
  if (store.get().character) {
    mountShell(root, renderMain);
    return;
  }
  const wrap = document.createElement('div');
  wrap.className = 'legal-standalone';
  const back = document.createElement('a');
  back.className = 'legal-standalone-back';
  back.href = '#/';
  back.textContent = '← Back to the gates';
  const main = document.createElement('main');
  main.className = 'legal-standalone-main';
  wrap.append(back, main);
  root.append(wrap);
  renderMain(main);
}

export function mountShell(
  root: HTMLElement,
  renderMain: (main: HTMLElement) => void,
): () => void {
  // Tear down the previous mount's subscription + listeners before building a
  // new one (see lastShellCleanup above).
  lastShellCleanup?.();
  lastShellCleanup = null;

  const char = store.get().character;
  if (!char) {
    // No character yet — caller should redirect to login.
    return () => {};
  }

  const shell = document.createElement('div');
  shell.className = 'shell';

  shell.append(buildHeader(char));
  shell.append(buildSidebar(char));

  const main = document.createElement('main');
  main.className = 'shell-main';
  shell.append(main);

  root.append(shell);
  renderMain(main);

  // Light up the action links whose resource is already full.
  refreshNavAttention(shell);

  // Per-page help: a small floating "?" that opens the Compendium at the exact
  // chapter explaining this page. Appended to `shell` (not `main`) so it
  // survives pages that rebuild their own content. Recreated on each route
  // mount, so it always points at the current page's chapter.
  const help = helpForCurrentRoute();
  if (help) {
    const helpLink = document.createElement('a');
    helpLink.className = 'page-help';
    helpLink.href = `#/wiki?s=${help.slug}`;
    helpLink.textContent = '?';
    helpLink.setAttribute('aria-label', `Open the Compendium: ${help.title}`);
    helpLink.title = `Help — ${help.title} (open the Compendium)`;
    shell.append(helpLink);
  }

  // ── Mobile drawer: dismiss without picking a page ──────────────────────────
  // Close on a tap of the dimmed backdrop, the Escape key, or a left-swipe.
  const closeDrawer = (): void => {
    shell.querySelector('.shell-sidebar')?.setAttribute('data-mobile-open', 'false');
    shell.setAttribute('data-mobile-nav', 'false');
  };
  // The backdrop is a fixed ::before on `.shell` sitting under the sidebar
  // (which has a higher z-index), so a tap anywhere off the drawer resolves its
  // hit-target to `.shell` itself — that's our "tap outside to close".
  shell.addEventListener('click', (e) => {
    if (shell.getAttribute('data-mobile-nav') === 'true' && e.target === shell) {
      closeDrawer();
    }
  });
  const onDrawerKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && shell.getAttribute('data-mobile-nav') === 'true') closeDrawer();
  };
  document.addEventListener('keydown', onDrawerKey);
  // Left-swipe on the open drawer closes it.
  let swipeX0 = 0;
  const sidebarEl = shell.querySelector('.shell-sidebar');
  sidebarEl?.addEventListener('touchstart', (e) => {
    swipeX0 = (e as TouchEvent).touches[0]?.clientX ?? 0;
  }, { passive: true });
  sidebarEl?.addEventListener('touchend', (e) => {
    const x1 = (e as TouchEvent).changedTouches[0]?.clientX ?? 0;
    if (x1 - swipeX0 < -45) closeDrawer();
  }, { passive: true });

  // Re-render header bars whenever the character updates, and patch the
  // sidebar crown total in place (the sidebar isn't rebuilt — that would
  // drop the mobile-drawer state — so we just update the one live number).
  const unsub = store.subscribe((s) => {
    if (!s.character) return;
    const newHeader = buildHeader(s.character);
    shell.replaceChild(newHeader, shell.firstElementChild!);
    const crownEl = shell.querySelector<HTMLSpanElement>('.sidebar-crowns-amount');
    if (crownEl) crownEl.textContent = formatCrownsFull(s.character.crowns);
    // Refresh the interest line immediately when bankInterest changes.
    const intEl = shell.querySelector<HTMLDivElement>('.sidebar-interest');
    if (intEl) intEl.innerHTML = renderInterestLine(s.bankInterest);
    // Refresh item cooldowns immediately (e.g. right after using an item).
    const cdEl = shell.querySelector<HTMLDivElement>('.sidebar-cooldowns');
    if (cdEl) cdEl.innerHTML = renderCooldownsPanel(s.character);
    // Refresh the chain tracker immediately when a fresh /me snapshot lands.
    const chainEl = shell.querySelector<HTMLDivElement>('.sidebar-chain');
    if (chainEl) chainEl.innerHTML = renderChainPanel(s.chain);
    // Re-evaluate the "ready to spend" glow against the new resource values.
    refreshNavAttention(shell);
  });

  // Wallet interest countdown: tick the displayed time every second, and when
  // it falls due, re-fetch /api/me once to settle the lump server-side and pull
  // the fresh balance + next payout time into the store.
  if (interestTicker !== null) window.clearInterval(interestTicker);
  interestTicker = window.setInterval(() => {
    const bi = store.get().bankInterest;
    const intEl = shell.querySelector<HTMLDivElement>('.sidebar-interest');
    if (intEl) intEl.innerHTML = renderInterestLine(bi);
    // Tick the item-cooldown countdowns every second too.
    const ch = store.get().character;
    const cdEl = shell.querySelector<HTMLDivElement>('.sidebar-cooldowns');
    if (cdEl && ch) cdEl.innerHTML = renderCooldownsPanel(ch);
    // Tick the House chain tracker (count decay + cold-out countdown).
    const chainEl = shell.querySelector<HTMLDivElement>('.sidebar-chain');
    if (chainEl) chainEl.innerHTML = renderChainPanel(store.get().chain);
    // Refresh the "ready to collect" activity status on a ~30s cadence, then
    // tick the glow so it appears the second a bar fills or a timer elapses.
    if (Date.now() - lastActivityFetch >= 30_000) {
      lastActivityFetch = Date.now();
      void refreshActivityStatus().then(() => refreshNavAttention(shell));
    }
    // Presence roster on a gentler 60s cadence (server caches 30s anyway).
    if (Date.now() - lastPresenceFetch >= 60_000) {
      lastPresenceFetch = Date.now();
      void refreshPresence().then(() => {
        const pEl = shell.querySelector<HTMLDivElement>('.sidebar-presence');
        if (pEl) pEl.innerHTML = renderPresencePanel();
      });
    }
    refreshNavAttention(shell);
    if (bi && Date.parse(bi.nextInterestAt) - Date.now() <= 0 && !interestRefreshing) {
      interestRefreshing = true;
      void api.me().finally(() => { interestRefreshing = false; });
    }
  }, 1000);

  const cleanup = () => {
    unsub();
    document.removeEventListener('keydown', onDrawerKey);
    if (interestTicker !== null) {
      window.clearInterval(interestTicker);
      interestTicker = null;
    }
  };
  // Remember this mount's cleanup so the NEXT mountShell can tear it down even
  // if the caller discards this return value (which the router does).
  lastShellCleanup = cleanup;
  return cleanup;
}
